import { useMemo, useState } from 'react'
import { DataType } from '../../universal-table/types/column.types'
import { GridColumn, SortDir, SortState } from '../types/grid.types'

interface Params<T> {
  rows: T[]
  columns: GridColumn<T>[]
  initial?: SortState<T>
}

const isNil = (v: unknown): boolean => v === null || v === undefined || v === ''

const compareByType = (a: unknown, b: unknown, dataType: DataType): number => {
  if (isNil(a) && isNil(b)) return 0
  if (isNil(a)) return 1
  if (isNil(b)) return -1

  switch (dataType) {
    case 'number':
    case 'float':
      return Number(a) - Number(b)
    case 'boolean':
      return a === b ? 0 : a ? 1 : -1
    case 'date':
      return new Date(a as string).getTime() - new Date(b as string).getTime()
    default:
      return String(a).localeCompare(String(b), 'he')
  }
}

/** Default direction when a new column is picked: text ascends, everything else descends. */
const defaultDirFor = (dataType?: DataType): SortDir =>
  dataType === 'string' || dataType === 'enum' ? 'asc' : 'desc'

export const useGridSort = <T,>({ rows, columns, initial = null }: Params<T>) => {
  const [sortState, setSortState] = useState<SortState<T>>(initial)

  const sortedRows = useMemo(() => {
    if (!sortState) return rows
    const col = columns.find((c) => c.key === sortState.key)
    if (!col) return rows
    const dir = sortState.dir === 'asc' ? 1 : -1
    // Stable sort: tie-break by original index.
    return rows
      .map((row, index) => ({ row, index }))
      .sort((x, y) => {
        const cmp = compareByType(x.row[sortState.key], y.row[sortState.key], col.dataType)
        return cmp !== 0 ? cmp * dir : x.index - y.index
      })
      .map(({ row }) => row)
  }, [rows, columns, sortState])

  const toggleSort = (key: keyof T) => {
    setSortState((prev) => {
      if (prev && prev.key === key) {
        return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      }
      const col = columns.find((c) => c.key === key)
      return { key, dir: defaultDirFor(col?.dataType) }
    })
  }

  return { sortState, sortedRows, toggleSort }
}

export default useGridSort
